/**
 * Popup plumbing for one-click Composio connects (DCR_OAUTH and any OAuth
 * scheme with no required fields). Shared by ComposioConnectionDialog (the
 * opener) and OAuthCallback (the page the popup returns to).
 *
 * The popup is recognised by its window name, never by `window.opener` alone:
 * a Studio tab opened by the host app also has an opener, and the same-tab
 * OAuth flow must not try to close it.
 *
 * Results travel over a BroadcastChannel rather than `window.opener`, because
 * some providers' login pages set Cross-Origin-Opener-Policy, which cuts the
 * opener link for good. The dialog also polls GET /composio/status, so a
 * missing channel only makes it slower, never stuck.
 */

export const OAUTH_POPUP_NAME = 'quadroid_composio_oauth';

const CHANNEL_NAME = 'quadroid-composio-oauth';
const POPUP_WIDTH = 560;
const POPUP_HEIGHT = 720;

/**
 * Open (or reuse) the login popup. Call it synchronously inside the click
 * handler, before any await, or the browser treats it as an unrequested popup.
 * Returns null when the popup is blocked.
 */
export const openOAuthPopup = (url = '') => {
  const left = Math.max(0, (window.screenX || 0) + ((window.outerWidth || POPUP_WIDTH) - POPUP_WIDTH) / 2);
  const top = Math.max(0, (window.screenY || 0) + ((window.outerHeight || POPUP_HEIGHT) - POPUP_HEIGHT) / 2);
  const features = `popup=yes,width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${Math.round(left)},top=${Math.round(top)}`;

  let popup = null;
  try {
    popup = window.open(url, OAUTH_POPUP_NAME, features);
  } catch {
    popup = null;
  }
  if (!popup) return null;

  if (!url) {
    // Blank placeholder while the connect request is in flight. A popup reused
    // from an earlier attempt may sit on another origin, so this can throw.
    try {
      popup.document.title = 'Connecting…';
      popup.document.body.style.font = '14px system-ui, sans-serif';
      popup.document.body.style.color = '#71717a';
      popup.document.body.style.padding = '24px';
      popup.document.body.textContent = 'Opening the login page…';
    } catch {
      /* cross-origin — leave it as is */
    }
  }
  try {
    popup.focus();
  } catch {
    /* ignore */
  }
  return popup;
};

/** True when this window is the login popup opened by openOAuthPopup. */
export const isOAuthPopupWindow = () => window.name === OAUTH_POPUP_NAME;

/** Tell the waiting dialog how the login ended: { status: 'success' | 'error', message }. */
export const publishOAuthResult = (result) => {
  if (typeof BroadcastChannel === 'undefined') return;
  try {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.postMessage(result);
    channel.close();
  } catch {
    /* status polling covers it */
  }
};

/** Listen for publishOAuthResult messages. Returns an unsubscribe function. */
export const subscribeOAuthResult = (handler) => {
  if (typeof BroadcastChannel === 'undefined') return () => {};
  let channel;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
  } catch {
    return () => {};
  }
  channel.onmessage = (event) => {
    if (event?.data && typeof event.data === 'object') handler(event.data);
  };
  return () => channel.close();
};
